import { API_ROOT, buildUrl, request } from './http';
import { asArray, asPage, asTransitions, readsAdvisory, unwrap } from './normalise';
import type { ProjectMember } from '../features/admin/membership';
import type {
  AdminUser,
  AuditEntry,
  CurrentUser,
  EnrolmentOffer,
  Id,
  ImpactDef,
  IssuedInvite,
  IssuedReset,
  LoginChallenge,
  LaneDef,
  Page,
  PriorityDef,
  Project,
  ResolutionDef,
  Sprint,
  StatusDef,
  StatusHistoryEntry,
  TicketComment,
  TicketDetail,
  TicketLink,
  TicketListItem,
  TicketQuery,
  Transition,
  TypeDef,
  Vocabularies,
} from './types';

/**
 * Every endpoint the MVP touches, in one file.
 *
 * ✅ RECONCILED against `tbs-planhub-api` at commit ab8f652 — paths, query-parameter names, request
 * bodies and response envelopes are read off `src/routes/*.ts`, not guessed. Where this app had
 * guessed differently, the guess lost; the differences are recorded in the README so the next reader
 * knows which of these were verified and which are still inference.
 */

// ── Auth — ⚠️ mounted at the ROOT, not under /api/v1 ─────────────────────────────────────────────

/**
 * A full-page navigation, not a fetch: the OAuth dance ends in a redirect carrying a Set-Cookie, and
 * XHR cannot follow that.
 *
 * ⚠️ `GET /auth/login` takes NO return_to — the API redirects to its configured `WEB_APP_URL` after
 * the callback. So a deep link is lost through sign-in. Recorded in the README rather than papered
 * over with a query parameter the API would ignore.
 */
export function gitlabAuthorizeUrl(): string {
  return buildUrl(API_ROOT, '/auth/login');
}

export async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser> {
  const body = await request<unknown>('/auth/me', { base: API_ROOT, signal });
  return unwrap<CurrentUser>(body, 'user', '/auth/me');
}

export async function logout(): Promise<void> {
  await request<unknown>('/auth/logout', { base: API_ROOT, method: 'POST' });
}

// ── Local login — password, then MANDATORY TOTP ───────────────────────────────────────────────────

/**
 * 🔴 THIS DOES NOT SIGN ANYBODY IN, and the return type is the proof: it resolves to a
 * `LoginChallenge`, never to a `CurrentUser`. The API cannot issue a session here — `createSession`
 * is not reachable from its login handler and a database trigger refuses a session row for a local
 * account that has not enrolled.
 *
 * ⭐ So this client cannot accidentally treat a password as sufficient: there is no user object to
 * put in the auth context, and the only thing to do with the result is show the next screen.
 */
export async function localLogin(email: string, password: string): Promise<LoginChallenge> {
  return request<LoginChallenge>('/auth/local/login', {
    base: API_ROOT,
    method: 'POST',
    body: { email, password },
  });
}

/**
 * Completes an ENROLLED account's login. The session cookie arrives on this response.
 *
 * ⚠️ The caller must `recheck()` the auth context afterwards rather than assuming: the cookie is
 * httpOnly, so this app cannot read it and has no way to know it was set except by asking `/auth/me`.
 */
export async function verifyTotp(challenge: string, code: string): Promise<void> {
  await request<unknown>('/auth/local/totp/verify', {
    base: API_ROOT,
    method: 'POST',
    body: { challenge, code },
  });
}

export async function fetchEnrolmentOffer(challenge: string): Promise<EnrolmentOffer> {
  return request<EnrolmentOffer>('/auth/local/totp/enrol', {
    base: API_ROOT,
    method: 'POST',
    body: { challenge },
  });
}

/** The first code proves the phone holds the secret; only then does the API store it. */
export async function verifyEnrolment(challenge: string, code: string): Promise<void> {
  await request<unknown>('/auth/local/totp/enrol/verify', {
    base: API_ROOT,
    method: 'POST',
    body: { challenge, code },
  });
}

/** Redeems an admin-issued invitation. ⭐ Returns an ENROLMENT challenge, never a session. */
export async function acceptInvite(token: string, password: string): Promise<LoginChallenge> {
  return request<LoginChallenge>('/auth/local/invite/accept', {
    base: API_ROOT,
    method: 'POST',
    body: { token, password },
  });
}

/** ⭐ Completing a reset does NOT clear the user's 2FA — see the API's `consumePasswordReset`. */
export async function completePasswordReset(token: string, password: string): Promise<void> {
  await request<unknown>('/auth/local/password/reset', {
    base: API_ROOT,
    method: 'POST',
    body: { token, password },
  });
}

// ── Admin ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * ⛔ THESE CALLS ARE NOT THE ACCESS CONTROL. The API checks `is_admin` on every one of them with its
 * own `requireAdmin` preHandler; hiding the nav entry is a courtesy to the 99% of users who are not
 * admins, not a security boundary. ⭐ Both exist on purpose and NEITHER is sufficient alone — a UI
 * check with no server check is theatre, and a server check with no UI check shows everyone a screen
 * that only 403s.
 */
export async function fetchAdminUsers(signal?: AbortSignal): Promise<AdminUser[]> {
  const body = await request<unknown>('/admin/users', { signal });
  return asArray<AdminUser>(body, '/admin/users');
}

export async function fetchAuditLog(signal?: AbortSignal): Promise<AuditEntry[]> {
  const body = await request<unknown>('/admin/audit', { signal, query: { limit: 50 } });
  return asArray<AuditEntry>(body, '/admin/audit');
}

export async function inviteUser(email: string, displayName: string): Promise<IssuedInvite> {
  return request<IssuedInvite>('/admin/users/invite', {
    method: 'POST',
    body: { email, displayName },
  });
}

export async function setUserActive(id: Id, active: boolean): Promise<void> {
  await request<unknown>(`/admin/users/${id}/active`, { method: 'POST', body: { active } });
}

/**
 * PROJECT MEMBERSHIP — PH-56. All three routes have existed since the beginning and this file
 * called none of them.
 *
 * ⚠️ THE READ IS NOT ADMIN-GATED BUT BOTH WRITES ARE (`requireAdmin` on the API). That asymmetry is
 * deliberate on the server — anyone may see who is on a project, only an admin may change it — so
 * the UI must not hide the list behind an admin check it does not need.
 *
 * ⭐ AND THIS IS THE ANSWER TO THE MOST CONFUSING ERROR IN PLANHUB. Every access failure today —
 * a release refused, the MCP's 403 writes, two humans unable to act — was the same missing
 * membership, and each time it surfaced as a puzzling permission error rather than as "this person
 * is in no project". These three calls are what let a screen say that plainly.
 */
export async function fetchProjectMembers(
  projectId: Id,
  signal?: AbortSignal,
): Promise<ProjectMember[]> {
  const body = await request<unknown>(`/projects/${projectId}/members`, { signal });
  return asArray<ProjectMember>(body, `/projects/${projectId}/members`);
}

/**
 * ⛔ `role` is ALWAYS SENT, never omitted to accept the server default. The API defaults it to
 * `member`, so omitting it works — but a caller reading this code could not tell whether the role
 * it chose was applied or silently replaced. Sending it makes the request state its intention.
 * ⚠️ The body is `.strict()`: `userId` and `role` are the only accepted keys.
 */
export async function addProjectMember(
  projectId: Id,
  userId: Id,
  role: 'member' | 'lead',
): Promise<void> {
  await request<unknown>(`/projects/${projectId}/members`, {
    method: 'POST',
    body: { userId, role },
  });
}

/**
 * ⚠️ A 404 here means "this user is not a member of this project" — the API says so in those words.
 * Callers must show it rather than swallow it: on a screen that just listed the membership, that
 * message means somebody else removed them first, which is worth knowing.
 */
export async function removeProjectMember(projectId: Id, userId: Id): Promise<void> {
  await request<unknown>(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
}

export async function setUserAdmin(id: Id, grant: boolean): Promise<void> {
  await request<unknown>(`/admin/users/${id}/admin`, { method: 'POST', body: { grant } });
}

/** ⭐ The ONLY way a second factor is ever removed, and the API writes an audit row for it. */
export async function forceTotpReenrol(id: Id): Promise<void> {
  await request<unknown>(`/admin/users/${id}/totp/reset`, { method: 'POST' });
}

export async function revokeUserSessions(id: Id): Promise<void> {
  await request<unknown>(`/admin/users/${id}/sessions/revoke`, { method: 'POST' });
}

export async function issuePasswordReset(id: Id): Promise<IssuedReset> {
  return request<IssuedReset>(`/admin/users/${id}/password-reset`, { method: 'POST' });
}

export interface ProjectDraft {
  key: string;
  name: string;
  kind: 'software' | 'research';
}

export async function createProject(draft: ProjectDraft): Promise<Project> {
  const body = await request<unknown>('/projects', { method: 'POST', body: draft });
  return unwrap<Project>(body, 'project', '/projects');
}

/**
 * ⛔ `key` IS ABSENT FROM THIS TYPE ON PURPOSE, and the API refuses it too. A project key is embedded
 * in every ticket reference ever written down, pasted into a merge request or sent in a message —
 * renaming it silently invalidates all of them. That is a migration, not a form field.
 */
export interface ProjectEdit {
  name?: string;
  kind?: 'software' | 'research';
}

export async function updateProject(id: Id, edit: ProjectEdit): Promise<Project> {
  const body = await request<unknown>(`/projects/${id}`, { method: 'PATCH', body: edit });
  return unwrap<Project>(body, 'project', `/projects/${id}`);
}

// ── Projects ──────────────────────────────────────────────────────────────────────────────────────

export async function fetchProjects(signal?: AbortSignal): Promise<Project[]> {
  const body = await request<unknown>('/projects', { signal });
  return asArray<Project>(body, '/projects');
}

/**
 * The sprint filter's options.
 *
 * 🔴 PER PROJECT, AND THERE IS NO ALL-PROJECTS FORM. Sprints belong to a project — `sprint.name` is
 * only UNIQUE within one (db/001), so two projects can each have a `v1.0.33` and merging them would
 * present two identical-looking options that filter to different things. ⇒ The filter is disabled
 * until a project is chosen, which is the honest shape of the data rather than a limitation.
 *
 * ⛔ The order is the SERVER'S. See `Sprint` in types.ts — do not sort the result here.
 */
export async function fetchSprints(projectId: Id, signal?: AbortSignal): Promise<Sprint[]> {
  const body = await request<unknown>(`/projects/${projectId}/sprints`, { signal });
  return asArray<Sprint>(body, `/projects/${projectId}/sprints`);
}

/**
 * `active` → `closed`, carrying the unfinished work out. ⭐ THE ONLY SPRINT WRITE THIS APP MAKES.
 *
 * ✅ Read off the API's `routes/sprints.ts` and `services/sprints.ts`, not guessed:
 *
 * · ONLY AN ACTIVE SPRINT CAN BE CLOSED. `closeSprint` refuses anything else with a 409 naming the
 *   state it found — `a 'planned' sprint cannot be closed; only an active sprint can`. ⇒ The UI
 *   offers the action on the active sprint alone, and the 409 is shown verbatim if it happens anyway
 *   (somebody else closed it first, which is exactly when a generic "failed" would be useless).
 *
 * · THE BODY IS OPTIONAL AND SO IS EVERY FIELD IN IT (`closeSprintBody` is `.default({})`).
 *   ⭐ `carryOverToSprintId` ABSENT and `null` MEAN THE SAME THING — back to the backlog — and the
 *   API's comment says that is deliberate, because "close it and put the leftovers back" is the
 *   common case and must need no body at all. ⇒ We OMIT the key rather than send an explicit null:
 *   identical on the server, and it keeps the request honest about what the user chose.
 *
 * · ⛔ THERE IS NO `carryOver: false`. The endpoint exists to prevent a closed sprint holding
 *   unfinished tickets — work that is invisible in every backlog — so "leave them where they are" is
 *   not a choice this dialog can offer.
 *
 * ⚠️ THE RESPONSE IS NOT WRAPPED. Every other sprint endpoint answers `{ sprint }`; this one returns
 *   `{ sprint, retro, carry_over }` at the TOP LEVEL. `unwrap(body, 'sprint', …)` would work for the
 *   first field and silently discard `carry_over`, which is the half that says what actually moved.
 */
export interface CloseSprintResult {
  sprint: Sprint;
  /** The snapshot. ⚠️ `committed_pts`/`completed_pts` are true only AT CLOSE — see the API's header. */
  retro: {
    sprint_id: Id;
    committed_pts: number | null;
    completed_pts: number | null;
    carried_over: number | null;
  };
  /** ⭐ What actually happened, from the server. `target_sprint_id: null` means the backlog. */
  carry_over: {
    target_sprint_id: Id | null;
    target_sprint_name: string | null;
    moved_ticket_ids: Id[];
    moved_count: number;
  };
}

export async function closeSprint(
  id: Id,
  carryOverToSprintId: Id | null,
): Promise<CloseSprintResult> {
  return request<CloseSprintResult>(`/sprints/${id}/close`, {
    method: 'POST',
    // ⭐ The path is `/sprints/:id/close`, NOT `/projects/:pid/sprints/:id/close`. The API's own note:
    //   an id is already unique, so repeating the project would be a second source of truth.
    body: carryOverToSprintId === null ? {} : { carryOverToSprintId },
  });
}

/**
 * ── PH-23 · THE REST OF THE SPRINT LIFECYCLE ─────────────────────────────────────────────────────
 *
 * 🔴 THE DEFECT WAS NOT A MISSING ENDPOINT. All three of these have existed in the API since
 *   `routes/sprints.ts` was written; this file simply had no caller for any of them, so the only
 *   sprint write the app could make was the LAST one. A team could close a sprint and then had no way
 *   to make another — Or found it on his phone, looking for the control Jira puts on the backlog.
 *
 * ✅ RECONCILED against `tbs-planhub-api/src/routes/sprints.ts` and `src/services/sprints.ts`, read,
 *   not guessed. What the source says, and what each line below is doing about it:
 *
 * · CREATE IS THE ONLY PROJECT-SCOPED PATH. `POST /projects/:id/sprints`; everything after a sprint
 *   exists addresses `/sprints/:id`. The API's own comment gives the reason — an id is already
 *   unique, so repeating the project is a second source of truth about which project a sprint is in.
 *
 * · ⛔ `state` IS NOT AN INPUT TO EITHER WRITE. `createSprintBody` has no `state` field and
 *   `UPDATABLE_SPRINT_COLUMNS` deliberately omits it. A sprint is born `planned` and the ONLY road to
 *   `active` is `/start`, which is where the one-active-per-project guard lives. ⇒ There is no
 *   `state` in `SprintDraftBody` or `SprintEdit`, so this client cannot even express the request that
 *   would bypass the guard.
 *
 * · ⛔ `project_id` IS NOT UPDATABLE EITHER, and for a stronger reason than tidiness: moving a sprint
 *   between projects would orphan every ticket in it.
 *
 * · EVERY WRITE IS GATED ON PROJECT MEMBERSHIP (`requireUser` + `assertMember`), NOT on admin. So
 *   these are offered to every signed-in member and the 403 — `only an active member of this project
 *   may modify its sprints` — is shown verbatim if the server disagrees.
 *
 * · THE RESPONSE IS `{ sprint }` FOR ALL THREE. ⚠️ Unlike `/close`, which answers `{sprint, retro,
 *   carry_over}` at the top level. That asymmetry is why these unwrap and `closeSprint` does not.
 *
 * ⚠️ CREATE ANSWERS 201, NOT 200. `request()` accepts any `response.ok`, so this needs no special
 *   handling — noted because a reader checking for `=== 200` somewhere would break it.
 */

/**
 * The body `createSprintBody` accepts, and nothing else.
 *
 * ⚠️ `goal` IS PART OF THE CONTRACT — `z.string().max(2000).nullish()`. It is easy to miss because
 *   `db/001` treats it as decoration and the section header only uses it as a tooltip, but a sprint
 *   goal is the one field that says what the sprint is FOR.
 * ⭐ `versionId` accepts a numeric STRING or a positive int (`numericId`); every id in this app is a
 *   string, which is the accepted half. The API refuses a version belonging to a different project
 *   with a 400 — `that version belongs to a different project` — which is why the picker is fed from
 *   this project's own versions.
 * ⛔ Optional fields are OMITTED rather than sent as `null`, the same rule `closeSprint` follows: the
 *   two are identical on the server, and an omitted key is a request that matches what the user
 *   actually chose.
 */
export interface SprintDraftBody {
  name: string;
  /** `YYYY-MM-DD`. ⛔ The API's regex is exactly that — an ISO instant is a 400. */
  startsOn: string;
  endsOn: string;
  goal?: string;
  versionId?: Id;
}

export async function createSprint(projectId: Id, draft: SprintDraftBody): Promise<Sprint> {
  const path = `/projects/${projectId}/sprints`;
  const body = await request<unknown>(path, { method: 'POST', body: draft });
  return unwrap<Sprint>(body, 'sprint', path);
}

/**
 * A PARTIAL update — `updateSprintBody` is `.partial()`, so an absent key means "leave it".
 *
 * 🔴 AND `null` MEANS SOMETHING DIFFERENT FROM ABSENT HERE, unlike on create. `goal` and `versionId`
 *   are `.nullable()` on the PATCH schema (not `.nullish()`), so `null` is the ONLY way to CLEAR
 *   them and omission is the only way to leave them alone. ⛔ Collapsing the two would make "remove
 *   this sprint's goal" impossible to express — the edit would silently keep the old value and look
 *   like a save that did nothing.
 */
export interface SprintEdit {
  name?: string;
  startsOn?: string;
  endsOn?: string;
  /** `null` CLEARS the goal. Absent leaves it. */
  goal?: string | null;
  /** `null` unlinks the version. Absent leaves it. */
  versionId?: Id | null;
}

export async function updateSprint(id: Id, edit: SprintEdit): Promise<Sprint> {
  const path = `/sprints/${id}`;
  const body = await request<unknown>(path, { method: 'PATCH', body: edit });
  return unwrap<Sprint>(body, 'sprint', path);
}

/**
 * `planned` → `active`.
 *
 * 🔴🔴 THE 409 IS THE WHOLE REASON THIS FUNCTION HAS A COMMENT. `db/001` carries
 *   `CREATE UNIQUE INDEX sprint_one_active_per_project … WHERE state = 'active'`, and `startSprint`
 *   puts TWO layers in front of it. The first is a pre-check that NAMES the sprint in the way:
 *
 *       this project already has an active sprint ('APFS v1.0.33'); close it before starting another
 *
 *   ⛔ THAT SENTENCE MUST REACH THE USER INTACT. The API's own header says the pre-check exists for
 *   USEFULNESS — "close Sprint 12 first" is the answer the person who pressed the button needs, and
 *   it is the half a generic "Could not start sprint" throws away. There is no other way for them to
 *   learn WHICH sprint to close: the blocker may be off screen, or in a section they have collapsed.
 *
 * ⚠️ The API can also answer 409 with `this sprint is already active` (somebody else started it
 *   first) or `a 'closed' sprint cannot be started; only a planned sprint can`. All three are
 *   surfaced the same way, verbatim, because all three tell the reader something different.
 *
 * ⭐ NO BODY AT ALL — the route reads only `:id`. `request` omits both the body and the Content-Type
 *   header when `body` is undefined, which is the same shape `forceTotpReenrol` has always sent.
 */
export async function startSprint(id: Id): Promise<Sprint> {
  const path = `/sprints/${id}/start`;
  const body = await request<unknown>(path, { method: 'POST' });
  return unwrap<Sprint>(body, 'sprint', path);
}

// ── Vocabularies ──────────────────────────────────────────────────────────────────────────────────

export async function fetchVocabularies(signal?: AbortSignal): Promise<Vocabularies> {
  const read = <T>(collection: string) =>
    request<unknown>(`/settings/${collection}`, { signal }).then((body) =>
      asArray<T>(body, `/settings/${collection}`),
    );

  const [statuses, lanes, types, priorities, impacts] = await Promise.all([
    read<StatusDef>('statuses'),
    read<LaneDef>('lanes'),
    read<TypeDef>('types'),
    read<PriorityDef>('priorities'),
    read<ImpactDef>('impacts'),
  ]);
  return { statuses, lanes, types, priorities, impacts };
}

/**
 * The Resolution column's labels — `GET /settings/resolutions`.
 *
 * ⭐ A SEPARATE CALL, not a sixth member of `fetchVocabularies`. Only the List view has a Resolution
 * column; the other screens would pay for a request they never read. ⛔ And it is fetched rather
 * than mapped from the code in JavaScript: `by_sprint → By Sprint` is a guess that works until
 * somebody adds `qa_waiver`, and `resolution_def` is editable in Settings without a deploy.
 */
export async function fetchResolutions(signal?: AbortSignal): Promise<ResolutionDef[]> {
  const body = await request<unknown>('/settings/resolutions', { signal });
  return asArray<ResolutionDef>(body, '/settings/resolutions');
}

// ── Tickets ───────────────────────────────────────────────────────────────────────────────────────

/** ⚠️ The API filters with camelCase names and paginates by limit/offset, not page/per_page. */
export async function fetchTickets(
  query: TicketQuery,
  signal?: AbortSignal,
): Promise<Page<TicketListItem>> {
  const body = await request<unknown>('/tickets', {
    signal,
    query: {
      projectId: query.projectId,
      status: query.status,
      assignee: query.assignee,
      // ⭐ `sprintId` — the API's own parameter name, accepted by `listTickets` since the beginning.
      sprintId: query.sprintId,
      /**
       * ⭐ `true` OR OMITTED, never the string "false".
       *
       * `buildUrl` drops null/undefined but stringifies `false` to `"false"`, which the API does
       * accept and treat as off — so this is tidiness rather than a bug fix. ⛔ What it does prevent
       * is a caller reading the network tab, seeing `noSprint=false`, and concluding the filter ran
       * and found nothing.
       */
      noSprint: query.noSprint ? true : null,
      /**
       * ⭐ SENT, NOT APPLIED HERE. `sort` goes to the server so page 2 is ordered by the same rule as
       * page 1; a client-side sort of the 50 rows it happens to hold would disagree with its own
       * pagination. ⚠️ `buildUrl` drops null/undefined, so an unset `sort` sends no parameter at all
       * and the API answers with its own default — see `TicketSort`.
       */
      sort: query.sort,
      /** ⚠️ Omitted means the ordering's own direction — NOT `desc`. Sending a default here would
       *  invert the backlog, whose `rank` ordering is ascending. */
      dir: query.dir,
      limit: query.per_page,
      offset: (query.page - 1) * query.per_page,
    },
  });
  return asPage<TicketListItem>(body, '/tickets', query);
}

/**
 * The children of one ticket.
 *
 * 🔴 THIS IS THE ONLY HONEST ANSWER TO "a parent on page 1 with a child on page 2". The backlog page
 * is a window over ONE flat `updated_at DESC` list, so a parent's children are not reliably in it;
 * asking for them by parent is a separate question with a complete answer.
 *
 * ⚠️ `total` is returned ALONGSIDE the items rather than folded into them: the API caps a page at
 * MAX_PAGE_SIZE = 100, so a parent with more children than that returns a short list and a true
 * count. ⛔ Reporting `items.length` as the count there would be a confident wrong number.
 */
export const SUBTASK_PAGE_SIZE = 100;

export async function fetchSubtasks(
  parentId: Id,
  signal?: AbortSignal,
): Promise<Page<TicketListItem>> {
  const body = await request<unknown>('/tickets', {
    signal,
    query: { parentId, limit: SUBTASK_PAGE_SIZE, offset: 0 },
  });
  return asPage<TicketListItem>(body, '/tickets?parentId', { page: 1, per_page: SUBTASK_PAGE_SIZE });
}

/**
 * Detail is FOUR calls, because the API splits them. They go in parallel and any one failing fails
 * the panel — ⛔ a panel that renders with its comments silently missing is the "stale looks current"
 * failure wearing a different hat.
 */
export async function fetchTicket(id: Id, signal?: AbortSignal): Promise<TicketDetail> {
  const [core, comments, links, subtasks] = await Promise.all([
    request<unknown>(`/tickets/${id}`, { signal }),
    request<unknown>(`/tickets/${id}/comments`, { signal }),
    request<unknown>(`/tickets/${id}/links`, { signal }),
    // ⭐ The same question the backlog asks, asked the same way — one shape of "children of X".
    fetchSubtasks(id, signal),
  ]);

  const path = `/tickets/${id}`;
  const ticket = unwrap<TicketListItem>(core, 'ticket', path);

  return {
    ...ticket,
    // `modules` rides alongside the ticket rather than inside it — db/001 keeps it in its own table.
    modules: asArray<string>({ items: unwrap<string[]>(core, 'modules', path) }, path),
    status_history: asArray<StatusHistoryEntry>({ items: unwrap(core, 'history', path) }, path),
    comments: asArray<TicketComment>(comments, `${path}/comments`),
    links: asArray<TicketLink>(links, `${path}/links`),
    subtasks: subtasks.items,
  };
}

/**
 * 🔴🔴 WHAT `GET /tickets/:id/transitions` MEANS CHANGED ON 2026-08-17 (PH-19).
 *
 * It is now the SUGGESTED next statuses — "what normally comes next" — and NOT the permitted ones.
 * Any ACTIVE status is a legal target whether or not it appears here (API `services/transitions.ts`:
 * *"THIS IS NOT A PERMISSION LIST AND ITS NAME SAYS SO … `performTransition` never consults it"*).
 *
 * ⇒ Two things travel together and must not be separated:
 *   · `suggested` — the list, still worth offering FIRST because it is genuinely the likely move
 *   · `advisory`  — whether THIS server treats the list as advice, read off `transitions_are_advisory`
 * ⛔ A caller that took the list without the flag would have to assume one semantics or the other,
 * which is exactly what PH-19 was.
 */
export interface TransitionAdvice {
  suggested: Transition[];
  /** `false` for a deployment predating PH-19 — see `readsAdvisory`; absence is not a promise. */
  advisory: boolean;
}

export async function fetchTransitionAdvice(
  id: Id,
  signal?: AbortSignal,
): Promise<TransitionAdvice> {
  const path = `/tickets/${id}/transitions`;
  const body = await request<unknown>(path, { signal });
  return { suggested: asTransitions(body, path), advisory: readsAdvisory(body) };
}

/**
 * The status dropdown's source of OPTIONS — the suggestions, offered first.
 *
 * ⚠️ It discards the advisory flag on purpose: a menu that lists the likely next statuses is useful
 * under either semantics, and the dropdown has never been the thing that REFUSED anything. ⛔ The
 * board must not use this — refusing a drop needs the flag, and `fetchTransitionAdvice` carries it.
 */
export async function fetchTransitions(id: Id, signal?: AbortSignal): Promise<Transition[]> {
  return (await fetchTransitionAdvice(id, signal)).suggested;
}

/** ⚠️ The body key is `toStatus` (camelCase), and the response returns the resulting ticket. */
export async function applyTransition(id: Id, toStatus: string, note?: string): Promise<void> {
  await request<unknown>(`/tickets/${id}/transition`, {
    method: 'POST',
    body: { toStatus, ...(note ? { note } : {}) },
  });
}

export async function addComment(ticketId: Id, body: string): Promise<void> {
  await request<unknown>(`/tickets/${ticketId}/comments`, { method: 'POST', body: { body } });
}

/**
 * Move a ticket in the backlog.
 *
 * ⭐ NEIGHBOURS, NOT AN INDEX. The API computes a sparse lexicographic `backlog_rank` between the two
 * ids it is given, so a reorder writes ONE row — an integer position would renumber everything below
 * the drop and turn one drag into N writes racing each other.
 *
 * ⚠️ `null` on either side is meaningful and is sent as such: `{beforeTicketId}` alone is "to the
 * top", `{afterTicketId}` alone is "below that one", and both null is "append".
 *
 * 🔴 THE 409 THIS CAN RAISE IS NOT A BUG, and callers must show it rather than swallow it. If a
 * neighbour has no `backlog_rank` yet the API answers `conflict: the … ticket has no backlog rank
 * yet; rank it first`. That is common in a project nobody has ordered by hand: `sort=rank` puts
 * unranked tickets last (NULLS LAST), so the bottom of a fresh backlog is exactly where they sit.
 */
export async function reorderTicket(
  id: Id,
  neighbours: { afterTicketId: Id | null; beforeTicketId: Id | null },
): Promise<void> {
  await request<unknown>(`/tickets/${id}/rank`, { method: 'PATCH', body: neighbours });
}

/**
 * CREATE A TICKET — PH-57.
 *
 * 🔴 THIS ROUTE HAS EXISTED SINCE THE BEGINNING AND THIS FILE NEVER CALLED IT. Every ticket in
 * PlanHub was created by an agent over the API or the MCP; a person using the web app could not make
 * one. The gap was invisible because the API was complete and its tickets closed correctly — the UI
 * half was tracked by nobody. Fourth instance of that shape (sprints PH-23, versions PH-28,
 * membership PH-56, tickets here).
 *
 * ⛔ THE BODY IS `.strict()` SINCE PH-33 — an unrecognised field is a 400 naming it, and the whole
 * request fails rather than the good half landing. Build the body with `createBody` from
 * `features/tickets/ticket-form.ts`, which sends only keys `createTicketBody` declares.
 *
 * ⭐ The new ticket is returned, and callers need it: it carries the `key` (`PH-59`) that the API
 * assigned, which is the only thing a person can use to find the ticket again.
 */
export async function createTicket(draft: unknown): Promise<TicketListItem> {
  const body = await request<unknown>('/tickets', { method: 'POST', body: draft });
  // ⚠️ `TicketListItem`, NOT `TicketDetail`. The write answers with the ROW — no comments, links or
  //   subtasks, because a ticket that has just been created has none and the API does not pretend
  //   otherwise. Typing this as `TicketDetail` would hand callers three fields that are always
  //   undefined, and the first `.comments.length` would throw at runtime with the types saying fine.
  return unwrap<TicketListItem>(body, 'ticket', '/tickets');
}

/**
 * EDIT A TICKET'S FIELDS — PH-57.
 *
 * ⛔ THIS CANNOT CHANGE STATUS, BY DESIGN, and a caller must not try. `updateTicketBody` refuses a
 * `status` key with a 400 that fails the ENTIRE request — deliberately, so that applying the legal
 * half of a request is never a 200 that silently did not do what it was asked. Status moves only
 * through `applyTransition`, which is what writes the history row.
 *
 * ⭐ SEND A DIFF, NOT THE WHOLE FORM. `editBody` produces one. PATCH is partial, so a caller that
 * sends every field writes back the values ITS form loaded and silently undoes anyone else's
 * concurrent edit.
 */
export async function updateTicket(id: Id, edit: unknown): Promise<TicketListItem> {
  const body = await request<unknown>(`/tickets/${id}`, { method: 'PATCH', body: edit });
  return unwrap<TicketListItem>(body, 'ticket', `/tickets/${id}`);
}

/**
 * PH-62 — the GitLab code path, finally reachable from the product.
 *
 * 🔴 These two routes have existed and worked since PH-8 and NOTHING CALLED EITHER. The issue path is
 *   live and proven (PH-29 created GitLab issue #1); branch and draft-MR simply had no trigger.
 *
 * ⛔ Neither takes a body. The ticket id IS the request — the API derives the branch name and the MR
 *   title from the ticket, so there is nothing for a caller to get wrong and nothing to keep in sync.
 */
export async function readGitlabState(ticketId: Id): Promise<GitlabCodeState> {
  const body = await request<Record<string, unknown>>(`/tickets/${ticketId}/gitlab`);
  return {
    issueIid: readIid(body, 'issue_iid'),
    branchName: typeof body.branch_name === 'string' ? body.branch_name : null,
    mergeRequestIid: readIid(body, 'merge_request_iid'),
    /**
     * ⚠️ DEFAULTS TO FALSE, i.e. to "we could not ask", NOT to available. An older deployment that
     *   omits the flag must read as UNKNOWN — the alternative offers write buttons on an assumption
     *   about a GitLab nobody confirmed was reachable. See `codeActionsFor`.
     */
    gitlabAvailable: body.gitlab_available === true,
  };
}

function readIid(body: Record<string, unknown>, key: string): string | null {
  const v = body[key];
  if (typeof v === 'string' && v.trim()) return v;
  if (typeof v === 'number') return String(v);
  return null;
}

/** ⭐ Path comes from `pathFor` so the hyphenated `merge-request` spelling has exactly one home. */
export async function createGitlabArtifact(ticketId: Id, action: CodeAction): Promise<void> {
  await request<unknown>(pathFor(ticketId, action), { method: 'POST' });
}import { pathFor, type CodeAction, type GitlabCodeState } from '../features/gitlab/code-actions';

/** A knowledge-graph document, rendered by the API. (PH-71/PH-72) */
export interface DocResponse {
  readonly path: string;
  readonly ref: string;
  /** HTML rendered by the API — the single renderer. ⛔ Do not render markdown in the web app. */
  readonly html: string;
  /**
   * The markdown source. **Absent unless `?format=md` was requested.**
   * ⭐ Optional on purpose: sending it unconditionally made 46% of every response a field this
   *   client never reads, on the mobile connection the feature exists to serve.
   */
  readonly content?: string;
  /**
   * The ticket this document declares in its `ticket:` front matter, resolved by the API.
   * ⛔ `null` is NORMAL, not an error: documents live in git and tickets in Postgres, so a document
   *   may name a ticket that was renumbered, deleted, or not created yet. The page renders without
   *   a back-link rather than failing.
   */
  readonly ticket: DocTicketRef | null;
  readonly renderedAt: string;
}

/** Enough of a ticket to render a link a reader can trust. */
export interface DocTicketRef {
  readonly id: Id;
  readonly key: string;
  readonly title: string;
  readonly status: string;
}

/** One entry in the document index. ⚠️ `title` is derived from the FILENAME — see below. */
export interface DocIndexEntry {
  readonly path: string;
  readonly title: string;
}

export interface DocIndexGroup {
  readonly root: string;
  readonly count: number;
  readonly documents: readonly DocIndexEntry[];
}

export interface DocIndexResponse {
  readonly ref: string;
  readonly groups: readonly DocIndexGroup[];
  readonly total: number;
}

/**
 * Every readable document, grouped by root. (PH-72 req 4)
 *
 * ⚠️ Titles come from FILENAMES, not from the documents. The vault holds ~1,681 markdown files and
 *   reading each one's heading would be ~1,681 GitLab calls per index load. A slightly worse title
 *   is the honest trade against an index nobody can afford to open.
 */
export async function fetchDocIndex(): Promise<DocIndexResponse> {
  return request<DocIndexResponse>('/docs/index');
}

/**
 * Fetch a knowledge-graph document.
 *
 * ⭐ Goes through `request()` so it carries `credentials: 'include'` from the WEB origin — which is
 *   the whole point of routing this through the frontend rather than linking the API directly. The
 *   session cookie is issued for this origin; a direct API link is not guaranteed to carry it,
 *   and on mobile it generally does not.
 */
export async function fetchDoc(path: string): Promise<DocResponse> {
  return request<DocResponse>(`/docs?path=${encodeURIComponent(path)}`);
}

